import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { formatDay, HANDOVER_TEXT_MAX, parseIsoDay } from '@wodi/domain';
import type { HandoverNoteView, HandoverNotes, WorklistRow } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  formatShortAge,
} from '@wodi/ui';
import { useRef, useState } from 'react';

import {
  handoverNoteInError,
  handoverNotesQuery,
  recordHandoverNote,
  wardWorklistQuery,
} from '../api/nursing.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { REASON_MIN_LENGTH } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { daysText, errorText, occupantLine } from './bed-board-page.tsx';
import styles from './nursing.module.css';
import { when } from './vitals-chart.tsx';

// The ward worklist and handover (docs/plans/phase2-ward-rounds-nursing.md §5): each patient
// in the ward with what is waiting for them, then the ward's handover notes. A restricted
// record shows its bed and day of stay only. Clinical danger (an unacknowledged critical
// result) is the only thing in `danger`.

const route = getRouteApi('/_app/wards/$wardId');

const SHIFT_WORDS = { day: 'Day shift', night: 'Night shift' } as const;

function shiftWords(n: { shiftDate: string; shift: 'day' | 'night' }): string {
  const day = parseIsoDay(n.shiftDate);
  return `${SHIFT_WORDS[n.shift]}, ${day.ok ? formatDay(day.value) : n.shiftDate}`;
}

/** Medicine doses waiting, in words: "2 due, 1 overdue" or "None due". */
export function doseWords(due: number, overdue: number): string {
  const parts = [
    due > 0 ? `${String(due)} due` : null,
    overdue > 0 ? `${String(overdue)} overdue` : null,
  ].filter((p) => p !== null);
  return parts.length === 0 ? 'None due' : parts.join(', ');
}

function PatientCell({ row }: { row: WorklistRow }) {
  const o = row.occupant;
  if (o.patient === null) return <span>{occupantLine(o)}</span>;
  return (
    <span>
      <Link to="/admissions/$admissionId" params={{ admissionId: o.admissionId }}>
        {o.patient.displayName}
      </Link>{' '}
      <span className="wodi-id">{o.patient.patientNumber}</span>
      <span
        className={styles.meta}
      >{` ${o.patient.sex} ${formatShortAge(o.patient.age, false)}`}</span>
    </span>
  );
}

function WorklistTable({ rows }: { rows: readonly WorklistRow[] }) {
  return (
    <Table flat dense aria-label="Patients in this ward">
      <TableHeader>
        <Column isRowHeader>Bed</Column>
        <Column>Patient</Column>
        <Column>Stay</Column>
        <Column>Pending results</Column>
        <Column>New orders</Column>
        <Column>Critical results</Column>
        <Column>Medicines</Column>
        <Column>Last vitals</Column>
        <Column>Ward round</Column>
      </TableHeader>
      <TableBody items={rows} renderEmptyState={() => 'No patients in this ward.'}>
        {(row) => {
          const c = row.clinical;
          return (
            <Row id={row.occupant.admissionId}>
              <Cell>{row.bed === null ? 'Waiting for a bed' : row.bed.label}</Cell>
              <Cell>
                <PatientCell row={row} />
              </Cell>
              <Cell>{daysText(row.occupant.daysAdmitted)}</Cell>
              <Cell>
                <span className={styles.numeric}>{c === null ? '' : String(c.pendingResults)}</span>
              </Cell>
              <Cell>
                <span className={styles.numeric}>{c === null ? '' : String(c.newOrders)}</span>
              </Cell>
              <Cell>
                {c === null ? (
                  ''
                ) : c.unacknowledgedCriticals > 0 ? (
                  <Tag state="danger">{`${String(c.unacknowledgedCriticals)} critical`}</Tag>
                ) : (
                  'None'
                )}
              </Cell>
              <Cell>{c === null ? '' : doseWords(c.dosesDue, c.dosesOverdue)}</Cell>
              <Cell>
                <span className={styles.numeric}>
                  {c === null ? '' : c.lastVitalsAt === null ? 'Not taken' : when(c.lastVitalsAt)}
                </span>
              </Cell>
              <Cell>
                {c === null ? (
                  ''
                ) : c.roundInProgress ? (
                  <Tag state="active">In progress</Tag>
                ) : c.roundedToday ? (
                  <Tag state="done">Seen today</Tag>
                ) : (
                  <Tag state="pending">Not seen today</Tag>
                )}
              </Cell>
            </Row>
          );
        }}
      </TableBody>
    </Table>
  );
}

function HandoverInError({
  note,
  wardId,
  onClose,
}: {
  note: HandoverNoteView;
  wardId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const mark = useMutation({
    mutationFn: () => handoverNoteInError(note.id, reason.trim()),
    onSuccess: (next) => {
      queryClient.setQueryData(['inpatient', 'handover', wardId], next);
      toasts.show({ title: 'Handover note entered in error' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Enter handover note in error"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <TextArea label="Why is it in error?" value={reason} onChange={setReason} rows={2} />
      {mark.error === null ? null : (
        <Notice tone="error">
          {errorText(mark.error, 'The note was not changed. Try again.')}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Keep note
        </Button>
        <Button
          variant="primary"
          isDisabled={reason.trim().length < REASON_MIN_LENGTH}
          isPending={mark.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            mark.mutate();
          }}
        >
          Enter in error
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Handover({ wardId, data }: { wardId: string; data: HandoverNotes }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const [text, setText] = useState('');
  const [marking, setMarking] = useState<HandoverNoteView | null>(null);
  const requestId = useRef(crypto.randomUUID());
  const mayWrite = session !== null && can(session, 'nursing.record');
  const save = useMutation({
    mutationFn: () => recordHandoverNote(wardId, { clientRequestId: requestId.current, text }),
    onSuccess: (next) => {
      queryClient.setQueryData(['inpatient', 'handover', wardId], next);
      requestId.current = crypto.randomUUID();
      setText('');
      toasts.show({ title: 'Handover note saved' });
    },
    onError: () => {
      requestId.current = crypto.randomUUID();
    },
  });
  return (
    <Section
      title="Handover"
      description={`Now: ${shiftWords(data.current)}. Shows this shift day and the one before. Don't write sensitive details here; they belong in the patient's record.`}
    >
      {mayWrite ? (
        <form
          className={styles.form}
          aria-label="Write a handover note"
          onSubmit={(event) => {
            event.preventDefault();
            if (online && text.trim() !== '') save.mutate();
          }}
        >
          <TextArea
            label="Handover note"
            value={text}
            onChange={setText}
            rows={4}
            description={`For the next shift. Up to ${String(HANDOVER_TEXT_MAX)} characters.`}
          />
          {save.error === null ? null : (
            <Notice tone="error">
              {errorText(save.error, 'The handover note was not saved. Try again.')}
            </Notice>
          )}
          <div className={styles.actions}>
            <Button
              type="submit"
              isDisabled={!online || text.trim() === ''}
              isPending={save.isPending}
              pendingLabel="Saving…"
            >
              Save handover note
            </Button>
          </div>
        </form>
      ) : null}
      {data.notes.length === 0 ? (
        <p className={styles.meta}>No handover notes for this ward in the last two shift days.</p>
      ) : (
        <ul className={styles.list} aria-label="Handover notes">
          {data.notes.map((note) => (
            <li key={note.id} className={styles.item} data-status={note.status}>
              <div className={styles.itemHead}>
                <span className={styles.meta}>
                  {`${shiftWords(note)} · ${when(note.recordedAt)} · ${note.recordedBy.displayName}`}
                </span>
                {note.status === 'entered_in_error' ? (
                  <Tag state="neutral">Entered in error</Tag>
                ) : mayWrite && note.recordedBy.id === session.user.id ? (
                  <Button
                    variant="quiet"
                    dense
                    isDisabled={!online}
                    onPress={() => {
                      setMarking(note);
                    }}
                  >
                    Enter in error
                  </Button>
                ) : null}
              </div>
              <p className={styles.text}>{note.text}</p>
              {note.statusReason === null ? null : (
                <p className={styles.meta}>{`Reason: ${note.statusReason}`}</p>
              )}
            </li>
          ))}
        </ul>
      )}
      {marking === null ? null : (
        <HandoverInError
          note={marking}
          wardId={wardId}
          onClose={() => {
            setMarking(null);
          }}
        />
      )}
    </Section>
  );
}

export function WardWorklistPage() {
  const { wardId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const allowed =
    session !== null && can(session, 'clinical.read') && can(session, 'inpatient.read');
  const worklist = useQuery({ ...wardWorklistQuery(wardId), enabled: allowed });
  const handover = useQuery({ ...handoverNotesQuery(wardId), enabled: allowed });

  if (!allowed) {
    return (
      <Notice tone="info">
        You don't have permission to see the ward worklist. Ask an administrator if you need it.
      </Notice>
    );
  }
  return (
    <div className={styles.page}>
      <PageHeader
        title={worklist.data?.ward.name ?? 'Ward'}
        description="Who is in the ward, what is waiting for them, and the handover."
        actions={
          <div className={styles.actions}>
            <Link to="/wards/$wardId/diets" params={{ wardId }}>
              Diets
            </Link>
            <Link to="/wards">Back to the wards</Link>
          </div>
        }
      />
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. The list shows what was loaded last; saving a handover note waits
          for the connection.
        </Notice>
      ) : null}
      <Section
        title="Patients"
        description={
          worklist.data === undefined
            ? 'Loading the patients in this ward.'
            : `New orders: placed in the last ${String(worklist.data.newOrderWindowHours)} hours (provisional). Refreshes every minute.`
        }
      >
        {worklist.isPending ? <LoadingBlock label="Loading the worklist" rows={5} /> : null}
        {worklist.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void worklist.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(worklist.error, "The worklist couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {worklist.isSuccess && worklist.data.rows.length === 0 ? (
          <EmptyState>No patients in this ward.</EmptyState>
        ) : null}
        {worklist.isSuccess && worklist.data.rows.length > 0 ? (
          <WorklistTable rows={worklist.data.rows} />
        ) : null}
      </Section>
      {handover.isPending ? <LoadingBlock label="Loading the handover" rows={3} /> : null}
      {handover.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void handover.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(handover.error, "The handover notes couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {handover.isSuccess ? <Handover wardId={wardId} data={handover.data} /> : null}
    </div>
  );
}
